import React, { createContext, useContext, useState, useEffect } from 'react'
import { authService } from '@/lib/auth'
import { STORAGE_KEY_NAMES, storage } from '@/lib/storage'
import type { AuthState, ResponseUser } from '@/types'

/**
 * Create the Authentication Context
 * This context will hold the auth state and make it available to all child components
 * Initially undefined - will be populated by AuthProvider
 */
const AuthContext = createContext<AuthState | undefined>(undefined)

/**
 * AuthProvider Component - Provides authentication state to entire app
 * 
 * This component wraps the entire application and manages authentication state.
 * It handles:
 * 1. Loading cached user data from localStorage on app startup
 * 2. Providing login/logout functionality to all child components
 * 3. Maintaining authentication state across page refreshes
 * 4. Showing loading spinner while initializing auth state
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Authentication state management
  const [user, setUser] = useState<ResponseUser | null>(null)   // Current logged-in user data (login format)
  const [isAuthenticated, setIsAuthenticated] = useState(false) // Authentication status flag
  const [isLoading, setIsLoading] = useState(true)              // Loading state during initialization

  /**
   * Initialize authentication state when app loads
   * 
   * This useEffect runs once when the AuthProvider mounts (app startup).
   * It checks if user was previously logged in by looking for:
   * 1. JWT token in localStorage
   * 2. User data in localStorage
   * 
   * If both exist, it assumes user is still logged in and restores the session.
   * This allows users to stay logged in even after page refresh or browser restart.
   */
  useEffect(() => {
    const checkAuthState = () => {
      const token = authService.getToken()         // Get JWT token from localStorage
      const cachedUser = authService.getCurrentUser() // Get user data from localStorage

      if (token && cachedUser) {
        // Both token and user data exist - restore the user session
        try {
          setUser(cachedUser)          // Restore user data to state
          setIsAuthenticated(true)     // Mark user as authenticated
        } catch (error) {
          // If anything goes wrong, clear the invalid auth data
          authService.logout()         // Clear localStorage and reset state
          setUser(null)
          setIsAuthenticated(false)
        }
      } else {
        // No valid auth data found - ensure user is logged out
        setUser(null)
        setIsAuthenticated(false)
      }
    }

    // Check auth state on mount
    checkAuthState()

    // Finished checking auth state - hide loading spinner
    setIsLoading(false)
  }, []) // Empty dependency array = run only once on mount

  /**
   * Listen for localStorage changes to detect when auth data is manually deleted
   * 
   * This handles cases where:
   * 1. User manually deletes localStorage via browser dev tools
   * 2. Another tab/window logs out the user
   * 3. localStorage is cleared by browser extensions or other scripts
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only react to changes in auth-related localStorage keys
      if (e.key === STORAGE_KEY_NAMES.TOKEN || e.key === STORAGE_KEY_NAMES.USER || e.key === null) {

        const token = authService.getToken()
        const cachedUser = authService.getCurrentUser()

        if (!token || !cachedUser) {
          // Auth data was removed - log user out immediately
          setUser(null)
          setIsAuthenticated(false)
        }
      }
    }

    // Listen for storage changes (works across tabs/windows)
    window.addEventListener('storage', handleStorageChange)

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  /**
   * Check for token expiration every 30 seconds
   * 
   * This proactively logs out users when their token expires,
   * even if they haven't made any API calls.
   */
  useEffect(() => {
    // Don't run if user is not authenticated
    if (!isAuthenticated) {
      return
    }

    const checkExpiration = () => {
      // Double-check token still exists (user might have logged out)
      if (!authService.getToken()) {
        return
      }

      if (storage.isTokenExpired()) {
        console.warn('Token expired - auto logout')
        setUser(null)
        setIsAuthenticated(false)
        storage.clearAuth()
        // Redirect to login page
        window.location.href = '/login'
      }
    }

    // Don't check immediately on mount to avoid race conditions with logout
    // Start checking after the first interval
    const intervalId = setInterval(checkExpiration, 30 * 1000)

    // Cleanup interval on unmount or when auth state changes
    return () => {
      clearInterval(intervalId)
    }
  }, [isAuthenticated])

  /**
   * Show loading screen while initializing authentication
   * 
   * During app startup, we need to check localStorage for existing auth data.
   * This prevents the app from briefly showing "not logged in" state before
   * discovering that the user is actually authenticated.
   * 
   * The loading screen is shown until we finish checking auth state.
   */
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    )
  }

  /**
   * Login function - Authenticate user with email and password
   * 
   * This function:
   * 1. Calls the backend API to verify credentials
   * 2. Receives JWT token and user data from server
   * 3. Stores token and user data in localStorage (handled by authService)
   * 4. Updates the React state to reflect logged-in status
   * 5. Throws error if login fails (to be handled by login form)
   */
  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password }) // Call backend API
      setUser(response.user)        // Store user data in React state
      setIsAuthenticated(true)      // Mark user as authenticated in React state
    } catch (error) {
      throw error                   // Re-throw error so login form can handle it
    }
  }

  /**
   * Logout function - Clear user session and redirect to login
   * 
   * This function:
   * 1. Calls backend API to invalidate the JWT token on server
   * 2. Removes token and user data from localStorage
   * 3. Clears React state (user data and authentication status)
   * 4. Components using this function should handle navigation/router invalidation
   */
  const logout = async () => {
    try {
      await authService.logout() // Call backend + clear localStorage
    } catch (error) {
      console.error('Logout error:', error)
      // Continue with local cleanup even if server logout fails
    } finally {
      setUser(null)           // Clear user data from React state
      setIsAuthenticated(false) // Mark user as not authenticated in React state
    }
  }

  /**
   * Update user data in context
   * 
   * This function allows components to update user information
   * (e.g., after editing profile, changing settings, etc.)
   * without requiring a full re-authentication.
   * 
   * Note: This only updates React state. The calling component
   * should also update localStorage and/or call backend API as needed.
   */
  const updateUser = (updatedUser: ResponseUser) => {
    setUser(updatedUser)    // Update user data in React state
  }

  /**
   * Validate current authentication state against localStorage
   * 
   * This function checks if the current auth state is still valid by
   * verifying that both token and user data exist in localStorage
   * and that the token has not expired.
   * If not, it immediately clears the auth state.
   * 
   * Returns true if auth is still valid, false if user was logged out.
   */
  const validateAuth = (): boolean => {
    const token = authService.getToken()
    const cachedUser = authService.getCurrentUser()

    // If no token exists, user is not authenticated (don't check expiration)
    // This prevents unnecessary warnings when user is already logged out
    if (!token || !cachedUser) {
      if (isAuthenticated) {
        // Auth state says authenticated but localStorage is empty
        setUser(null)
        setIsAuthenticated(false)
      }
      return false
    }

    // Check if token is expired (only if token exists)
    if (storage.isTokenExpired()) {
      console.warn('Token expired during validation')
      setUser(null)
      setIsAuthenticated(false)
      storage.clearAuth()
      return false
    }

    return isAuthenticated && !!token && !!cachedUser
  }

  /**
   * Provide authentication state and functions to all child components
   * 
   * The AuthContext.Provider makes the auth state and functions available
   * to any component in the app that calls useAuth().
   * 
   * This includes:
   * - Current authentication status
   * - User data
   * - Login/logout functions
   * - User update function
   * - Loading state
   */
  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, updateUser, validateAuth, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * useAuth Hook - Access authentication state from any component
 * 
 * This custom hook provides a convenient way for components to access
 * the authentication context. It includes safety checks to ensure
 * the hook is only used within components that are wrapped by AuthProvider.
 * 
 * Usage example:
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth()
 *   
 *   if (!isAuthenticated) {
 *     return <div>Please log in</div>
 *   }
 *   
 *   return <div>Welcome, {user.firstName}!</div>
 * }
 * ```
 */
export function useAuth() {
  const context = useContext(AuthContext)

  // Safety check: ensure useAuth is only called within AuthProvider
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}